import { useMemo, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useAccountStore } from '@/stores/account-store'
import { usePlanTierStore } from '@/stores/plan-tier-store'
import { useGetUsageQuery, useGetEntitlementsQuery } from '@/services/api/users-api'
import {
  pickLiveSubscription,
  resolvePlanTier,
  tierMeetsRequirement,
  IS_MOCK_AUTH,
  DEFAULT_MAX_CUSTOM_LAYOUTS,
  DEFAULT_SCANNER_ROW_LIMIT,
  DEFAULT_SCANNER_POLL_INTERVAL,
  DEFAULT_SCANNER_PANELS_PER_LAYOUT,
  DEFAULT_MAX_EXPRESSIONS_PER_TIER,
  FEATURE_REQUIREMENTS,
} from './plan-constraint-utils'
import type { Feature } from './plan-constraint-utils'
import { readDevPlanOverride } from './dev-plan-override'
import { PlanConstraintContext } from './plan-constraint-hooks'
import type { PlanConstraintContextValue } from './plan-constraint-hooks'

/**
 * The server sends `null` for an unlimited limit; the app compares against
 * numbers (`saved.length < maxCustomLayouts`), so null becomes `Infinity`.
 * A `0` is a real ceiling of zero and passes straight through.
 */
function resolveLimit(served: number | null | undefined, fallback: number): number {
  if (served === undefined) return fallback
  return served === null ? Infinity : served
}

/**
 * Plan constraint provider: resolves the plan tier (`resolvePlanTier`: dev
 * override, then the entitlements payload's plan_level, then the live
 * subscription) and every plan limit for the app below it.
 *
 * Limits come from `GET /v2/users/me/entitlements`, which the server owns
 * (`edge-user-api/services/entitlements.py`). Until that payload lands — and in
 * mock-auth dev mode, where it is never fetched — the DEFAULT_* tables in
 * `plan-constraint-utils.ts` stand in for it. The frontend deliberately keeps no
 * per-plan numbers beyond those defaults; changing a limit is a backend edit.
 *
 * In dev mode (MockAuth) the derived tier is the top tier, so nothing is gated.
 */
export function PlanConstraintProvider({ children }: { children: ReactNode }) {
  const subscriptions = useAccountStore((s) => s.user?.subscriptions)
  // The live row (active/trialing/past_due, highest tier), not `subscriptions[0]`,
  // which can be a canceled row listed ahead of the current one. `planName` is
  // display-only; the tier comes from `resolvePlanTier` below.
  const planDetails = pickLiveSubscription(subscriptions)?.planDetails
  const planName = planDetails?.name ?? null

  // Dev preview: pretend to be another tier so the gates are visible from the
  // inside (a real account is Pro+, mock auth is Ultimate). It must also drop
  // the served payload, because `hasAccess` and every resolved limit prefer it:
  // keeping Pro+'s feature list would leave every gate open under a Basic
  // override. The DEFAULT_* tables then stand in, which is what they are for.
  const devOverride = readDevPlanOverride()

  // Fetch server-side usage and entitlements (skip in mock auth mode). Focus
  // refetch picks up a plan change made in another tab or the Stripe portal.
  const { data: usage } = useGetUsageQuery(undefined, { skip: IS_MOCK_AUTH })
  const { data: entitlementsResponse } = useGetEntitlementsQuery(undefined, {
    skip: IS_MOCK_AUTH,
    refetchOnFocus: true,
  })
  const entitlements = devOverride ? null : (entitlementsResponse?.entitlements ?? null)

  // One tier for every gate: the payload's resolved plan_level is the row every
  // limit and `hasAccess` read, so the tier follows it once it lands.
  const planTier = resolvePlanTier({
    override: devOverride,
    servedPlanLevel: entitlementsResponse?.plan_level,
    subscriptions,
  })

  // Publish for the gates that run outside React (see stores/plan-tier-store).
  // Cleared on unmount so a signed-out session cannot leak its tier.
  useEffect(() => {
    usePlanTierStore.setState({ planTier })
  }, [planTier])
  useEffect(() => () => usePlanTierStore.setState({ planTier: null }), [])

  const value = useMemo<PlanConstraintContextValue>(
    () => ({
      planTier,
      planName,
      entitlements,
      maxCustomLayouts: resolveLimit(
        entitlements?.custom_layouts_max,
        DEFAULT_MAX_CUSTOM_LAYOUTS[planTier],
      ),
      scannerRowLimit: resolveLimit(
        entitlements?.scanner_rows_max,
        DEFAULT_SCANNER_ROW_LIMIT[planTier],
      ),
      scannerPollIntervalMs:
        entitlements?.scanner_poll_interval_ms ?? DEFAULT_SCANNER_POLL_INTERVAL[planTier],
      scannerPanelsPerLayoutMax: resolveLimit(
        entitlements?.scanner_panels_per_layout_max,
        DEFAULT_SCANNER_PANELS_PER_LAYOUT[planTier],
      ),
      maxExpressions: resolveLimit(
        entitlements?.expressions_max,
        DEFAULT_MAX_EXPRESSIONS_PER_TIER[planTier],
      ),
      usage: usage ?? null,
      hasAccess: (feature: Feature): boolean => {
        // The served list is authoritative: it may grant a feature the local
        // table would refuse, or withhold one the table would allow.
        if (entitlements) return entitlements.features.includes(feature)
        return tierMeetsRequirement(planTier, FEATURE_REQUIREMENTS[feature])
      },
    }),
    [planTier, planName, usage, entitlements],
  )

  return <PlanConstraintContext value={value}>{children}</PlanConstraintContext>
}
